import { rngSeed } from '@vpgc/core/rng';
import { CAMERA, PLANET, WORLD_RULES } from '../domain/content.js';
import type { GameState, SaveJson } from '../contract.js';
import { PLANET_RUNNER_SCHEMA_VERSION } from '../contract.js';

export function createInitialState(seed: number): GameState {
  return {
    schemaVersion: PLANET_RUNNER_SCHEMA_VERSION,
    seed: seed | 0,
    rng: rngSeed(seed),
    tick: 0,
    cameraZoom: CAMERA.defaultZoom,
    cameraMode: 'locked',
    cameraAngle: PLANET.startAngle,
    minimapUnlocked: false,
    statusMessage: 'none',
    statusTicks: 0,
    swimStamina: WORLD_RULES.swimStaminaMax,
    inWater: false,
    homeReturnTicks: 0,
    playerSurfaceDistance: WORLD_RULES.homeSurfaceDistance,
    player: {
      angle: PLANET.startAngle,
      altitude: 0,
      tangentVelocity: 0,
      radialVelocity: 0,
      facing: 1,
      grounded: true,
      coyoteTicks: 0,
      jumpBufferTicks: 0,
      walkCycle: 0,
      landingTicks: 0,
      idleTicks: 0,
      blockedTicks: 0,
    },
    discoveries: {
      leftActionSeen: false,
      rightActionSeen: false,
    },
  };
}

export function serialize(state: GameState): SaveJson {
  return {
    schemaVersion: PLANET_RUNNER_SCHEMA_VERSION,
    seed: state.seed,
    rng: state.rng,
    tick: state.tick,
    cameraZoom: state.cameraZoom,
    cameraMode: state.cameraMode,
    cameraAngle: state.cameraAngle,
    minimapUnlocked: state.minimapUnlocked,
    statusMessage: state.statusMessage,
    statusTicks: state.statusTicks,
    swimStamina: state.swimStamina,
    inWater: state.inWater,
    homeReturnTicks: state.homeReturnTicks,
    playerSurfaceDistance: state.playerSurfaceDistance,
    player: {
      angle: state.player.angle,
      altitude: state.player.altitude,
      tangentVelocity: state.player.tangentVelocity,
      radialVelocity: state.player.radialVelocity,
      facing: state.player.facing,
      grounded: state.player.grounded,
      coyoteTicks: state.player.coyoteTicks,
      jumpBufferTicks: state.player.jumpBufferTicks,
      walkCycle: state.player.walkCycle,
      landingTicks: state.player.landingTicks,
      idleTicks: state.player.idleTicks,
      blockedTicks: state.player.blockedTicks,
    },
    discoveries: {
      leftActionSeen: state.discoveries.leftActionSeen,
      rightActionSeen: state.discoveries.rightActionSeen,
    },
  };
}

export function deserialize(save: SaveJson): GameState {
  const migrated = migrateSave(save);
  return {
    schemaVersion: PLANET_RUNNER_SCHEMA_VERSION,
    seed: migrated.seed | 0,
    rng: migrated.rng | 0,
    tick: migrated.tick | 0,
    cameraZoom: migrated.cameraZoom,
    cameraMode: migrated.cameraMode,
    cameraAngle: migrated.cameraAngle,
    minimapUnlocked: migrated.minimapUnlocked,
    statusMessage: migrated.statusMessage,
    statusTicks: migrated.statusTicks,
    swimStamina: migrated.swimStamina,
    inWater: migrated.inWater,
    homeReturnTicks: migrated.homeReturnTicks,
    playerSurfaceDistance: migrated.playerSurfaceDistance,
    player: {
      angle: migrated.player.angle,
      altitude: migrated.player.altitude,
      tangentVelocity: migrated.player.tangentVelocity,
      radialVelocity: migrated.player.radialVelocity,
      facing: migrated.player.facing,
      grounded: migrated.player.grounded,
      coyoteTicks: migrated.player.coyoteTicks,
      jumpBufferTicks: migrated.player.jumpBufferTicks,
      walkCycle: migrated.player.walkCycle,
      landingTicks: migrated.player.landingTicks,
      idleTicks: migrated.player.idleTicks,
      blockedTicks: migrated.player.blockedTicks,
    },
    discoveries: {
      leftActionSeen: migrated.discoveries.leftActionSeen,
      rightActionSeen: migrated.discoveries.rightActionSeen,
    },
  };
}

export function migrateSave(save: SaveJson): SaveJson {
  if (save.schemaVersion !== PLANET_RUNNER_SCHEMA_VERSION) {
    throw new Error(`Unsupported save schema ${save.schemaVersion}`);
  }
  return save;
}
